import React from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { ArrowForward, SellOutlined, TuneOutlined } from '@mui/icons-material';
import { tokens } from '../theme/tokens';
import { StatusChip, StatusTone } from './ui/StatusChip';
import { ChangeField, ChangeStatus, MessageChange } from './aiAgentModel';

/**
 * The change card under an Ask Mozi reply (states 1c and 1d, flag `mozi_change_cards`).
 * Presentational: AIAgentSidebar owns the status and calls the apply and undo endpoints.
 * Copy is from docs/internal/MOZI_CHAT_DRAWER.md.
 */

export interface AiChangeCardProps {
  change: MessageChange;
  status: ChangeStatus;
  error?: string;
  /** A failed outcome has a captured before value: offer Restore beside the primary button (which retries). */
  canUndo?: boolean;
  verified?: boolean;
  /** Price read back from the store after the last apply. */
  price?: number | null;
  userName?: string;
  onApply: () => void;
  onUndo: () => void;
  onSecondary: () => void;
}

const COPY = {
  sync_fields: {
    primary: 'Apply change',
    applying: 'Saving to your Shopify integration',
    appliedChip: 'Applied',
    undo: 'Undo',
  },
  product_price: {
    primary: 'Fix and push to store',
    applying: 'Pushing to Shopify',
    appliedChip: 'Live on store',
    undo: 'Undo',
  },
} as const;

export const formatMoney = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatTime = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const fieldNames = (change: MessageChange): string =>
  change.payload.changes
    .filter((c) => c.from !== c.to)
    .map((c) => c.label || c.field)
    .join(', ');

/** The consequence line, with the real next-sync time and product count filled from the proposal. */
export const consequenceLine = (change: MessageChange): string => {
  const time = formatTime(change.payload.nextSyncAt);
  if (change.type === 'sync_fields') {
    const count = change.payload.productCount ?? 0;
    return `The next sync${time ? `, at ${time},` : ''} writes ${fieldNames(change)} to ${count.toLocaleString('en-US')} ${count === 1 ? 'product' : 'products'}. Nothing changes until you apply.`;
  }
  return `Pushes to Shopify now, for this product only. The rest of your catalog waits for the ${time ? `${time} ` : 'next '}sync.`;
};

const title = (change: MessageChange): string =>
  change.type === 'sync_fields'
    ? `Add ${fieldNames(change)} to sync fields`
    : `Fix the price of ${change.payload.productName || 'this product'}`;

const rowValue = (value: ChangeField['from']): string =>
  typeof value === 'number' ? formatMoney(value) : value === null ? 'Not set' : value ? 'On' : 'Off';

const FieldRow: React.FC<{ change: ChangeField; type: MessageChange['type'] }> = ({ change, type }) => {
  const same = change.from === change.to;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        p: '9px 12px',
        borderTop: `1px solid ${tokens.line.hairline}`,
        fontSize: 13,
      }}
    >
      <Box sx={{ color: tokens.ink.body, fontWeight: 500 }}>{change.label || change.field}</Box>
      {same ? (
        <Box sx={{ color: tokens.ink.muted }}>No change</Box>
      ) : type === 'sync_fields' ? (
        <StatusChip label="Added" tone="positive" />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box sx={{ color: tokens.ink.muted, textDecoration: 'line-through' }}>{rowValue(change.from)}</Box>
          <ArrowForward sx={{ fontSize: 14, color: tokens.ink.faint }} />
          <Box sx={{ color: tokens.ink.primary, fontWeight: 600 }}>{rowValue(change.to)}</Box>
        </Box>
      )}
    </Box>
  );
};

const buttonSx = {
  height: 32,
  px: '12px',
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: `${tokens.radius.small}px`,
} as const;

const AiChangeCard: React.FC<AiChangeCardProps> = ({
  change,
  status,
  error,
  canUndo,
  verified,
  price,
  userName,
  onApply,
  onUndo,
  onSecondary,
}) => {
  const copy = COPY[change.type];
  const scope =
    change.type === 'sync_fields'
      ? change.payload.integrationName || 'Shopify'
      : [change.payload.productName, change.payload.integrationName].filter(Boolean).join(' · ');
  const first = change.payload.changes[0];
  const restoreLabel =
    change.type === 'product_price' && typeof first?.from === 'number' ? `Restore ${formatMoney(first.from)}` : copy.undo;

  const chip: { label: string; tone: StatusTone } =
    status === 'applied'
      ? { label: copy.appliedChip, tone: 'positive' }
      : status === 'failed'
        ? { label: "Didn't apply", tone: 'problem' }
        : status === 'applying'
          ? { label: 'Applying', tone: 'info' }
          : status === 'undone'
            ? { label: 'Undone', tone: 'neutral' }
            : { label: 'Proposed', tone: 'caution' };

  const appliedLine =
    change.type === 'product_price' && verified && typeof price === 'number'
      ? `Verified on Shopify · ${formatMoney(price)} · just now`
      : `Applied${userName ? ` for ${userName}` : ''} · just now`;

  return (
    <Box
      data-testid="ai-change-card"
      sx={{
        border: `1px solid ${tokens.line.default}`,
        borderRadius: `${tokens.radius.card}px`,
        bgcolor: tokens.surface.card,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 12px', bgcolor: tokens.surface.sunken }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: `${tokens.radius.small}px`,
            bgcolor: tokens.accent.tint,
            color: tokens.accent.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {change.type === 'sync_fields' ? <TuneOutlined sx={{ fontSize: 17 }} /> : <SellOutlined sx={{ fontSize: 17 }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: 13.5, fontWeight: 600, color: tokens.ink.primary, lineHeight: 1.3 }}>
            {title(change)}
          </Typography>
          <Typography component="div" sx={{ fontSize: 12, color: tokens.ink.muted, lineHeight: 1.3 }} noWrap>
            {scope}
          </Typography>
        </Box>
        <StatusChip label={chip.label} tone={chip.tone} />
      </Box>

      {change.payload.changes.map((c) => (
        <FieldRow key={c.field} change={c} type={change.type} />
      ))}

      <Box sx={{ p: '10px 12px 12px', borderTop: `1px solid ${tokens.line.hairline}`, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {status === 'applying' ? (
          <Box role="status" aria-label={copy.applying}>
            <Box sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt, mb: '6px' }}>{copy.applying}</Box>
            <LinearProgress />
          </Box>
        ) : status === 'applied' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <Box sx={{ fontSize: 12.5, color: tokens.signal.positive.ink }}>{appliedLine}</Box>
            {change.canApply && (
              <Button onClick={onUndo} sx={{ ...buttonSx, color: tokens.accent.hover, '&:hover': { bgcolor: tokens.accent.tint } }}>
                {restoreLabel}
              </Button>
            )}
          </Box>
        ) : status === 'undone' ? (
          <Box sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
            {change.type === 'product_price' && typeof price === 'number' ? `Restored ${formatMoney(price)} · just now` : 'Restored the previous values · just now'}
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: 12.5, lineHeight: 1.5, color: status === 'failed' ? tokens.signal.problem.ink : tokens.ink.bodyAlt }}>
              {status === 'failed'
                ? error || "Mozi couldn't make that change."
                : change.canApply
                  ? consequenceLine(change)
                  : `Only an ${change.whoCan} can apply this change.`}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {change.canApply && (
                <Button
                  onClick={onApply}
                  variant="contained"
                  disableElevation
                  sx={{ ...buttonSx, bgcolor: tokens.accent.main, '&:hover': { bgcolor: tokens.accent.hover } }}
                >
                  {copy.primary}
                </Button>
              )}
              {status === 'failed' && canUndo && change.canApply && (
                <Button onClick={onUndo} sx={{ ...buttonSx, color: tokens.accent.hover, '&:hover': { bgcolor: tokens.accent.tint } }}>
                  {restoreLabel}
                </Button>
              )}
              <Button
                onClick={onSecondary}
                sx={{
                  ...buttonSx,
                  color: tokens.accent.hover,
                  border: `1px solid ${tokens.line.strong}`,
                  '&:hover': { bgcolor: tokens.accent.tint, borderColor: tokens.accent.border },
                }}
              >
                {change.secondary.label}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AiChangeCard;
